import React, { useMemo, useRef } from "react";
import { Button, Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger, useDisclosure } from "@chakra-ui/react";
import { ChakraFAIcon } from "@/components/ui/Icon";
import useClickOutside from "@/js/utils/outsideClickUtils";
import { floatingToolbarGhostButtonProps, floatingToolbarPopoverContentProps, floatingToolbarBg } from "./FloatingToolbar";

interface ToolbarMenuProps {
	label: string;
	icon: string;
	isDisabled?: boolean;
	buttonTestId?: string;
	popupId?: string;
	onButtonClick?: () => void;
	children: React.ReactNode;
}

/**
 * A floating-toolbar popup that reliably closes when the user clicks anywhere
 * outside it.
 *
 * Chakra's built-in close-on-blur relies on document `mousedown`/`mouseup` events,
 * which the grid's pointer handling can swallow, leaving the popup open. We
 * therefore control open state ourselves and close it from a capture-phase
 * `pointerdown` listener, which fires before the grid can suppress it.
 */
export function ToolbarMenu({
	label,
	icon,
	isDisabled,
	buttonTestId,
	popupId = "seatplan-toolbar-menu-popup",
	onButtonClick,
	children,
}: ToolbarMenuProps): JSX.Element {
	const { isOpen, onOpen, onClose } = useDisclosure();
	const buttonRef = useRef<HTMLButtonElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const refs = useMemo(() => [buttonRef, contentRef], []);
	useClickOutside(refs, onClose, "pointerdown");

	return (
		<Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="top-start" gutter={14} closeOnBlur={false}>
			<PopoverTrigger>
				<Button
					ref={buttonRef}
					{...floatingToolbarGhostButtonProps}
					isDisabled={isDisabled}
					leftIcon={<ChakraFAIcon icon={icon} variant="solid" boxSize={4} />}
					data-testid={buttonTestId}
					onClick={onButtonClick}
				>
					{label}
				</Button>
			</PopoverTrigger>
			<PopoverContent ref={contentRef} {...floatingToolbarPopoverContentProps} id={popupId} data-testid={popupId}>
				<PopoverArrow bg={floatingToolbarBg} shadow="none" />
				<PopoverBody p={2} display="flex" flexDirection="column" gap={1} onClick={onClose}>
					{children}
				</PopoverBody>
			</PopoverContent>
		</Popover>
	);
}
